import { AlertCircle, Globe, RefreshCw, Wand2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
    entityLoading: boolean;
    entityError: boolean;
    entityId: number | null | undefined;
    isPreviewReady: boolean;
    livePreviewUrl: string;
    isAgentThinking: boolean;
    isPublishing: boolean;
    showBuildingAnimation: boolean;
    onRefreshPreview: (url?: string) => void;
    // Imperative handle so parent can trigger a refresh after SSE preview_ready
    previewRefreshTrigger: { url?: string } | null;
}

export default function PreviewPanel({
    entityLoading,
    entityError,
    entityId,
    isPreviewReady,
    livePreviewUrl,
    isAgentThinking,
    isPublishing,
    showBuildingAnimation,
    onRefreshPreview,
    previewRefreshTrigger,
}: Props) {
    const navigate = useNavigate();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const isLoadingRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [iframeError, setIframeError] = useState(false);
    const [iframeSrc, setIframeSrc] = useState('about:blank');

    const clearWatchdog = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const startWatchdog = () => {
        clearWatchdog();
        timeoutRef.current = setTimeout(() => {
            if (isLoadingRef.current) {
                console.warn('[PreviewPanel] iframe load timed out');
                setIframeError(true);
                setIsIframeLoading(false);
                isLoadingRef.current = false;
            }
        }, 120_000);
    };

    const withCacheBuster = (url: string) => {
        try {
            const next = new URL(url, window.location.href);
            next.searchParams.set('previewUpdatedAt', Date.now().toString());
            return next.toString();
        } catch {
            return url;
        }
    };

    const loadUrl = useCallback((url: string) => {
        if (!url || url === 'about:blank') return;
        setIsIframeLoading(true);
        isLoadingRef.current = true;
        setIframeError(false);
        setIframeSrc(withCacheBuster(url));
        startWatchdog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial load when entityId becomes available
    useEffect(() => {
        if (entityId !== undefined && entityId !== null && livePreviewUrl && livePreviewUrl !== 'about:blank') {
            loadUrl(livePreviewUrl);
        }
        return clearWatchdog;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, livePreviewUrl]);

    // React to SSE preview_ready trigger from parent
    useEffect(() => {
        if (!previewRefreshTrigger) return;
        const url = previewRefreshTrigger.url || livePreviewUrl;
        if (url && url !== 'about:blank') loadUrl(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewRefreshTrigger]);

    const handleLoad = () => {
        clearWatchdog();
        setIsIframeLoading(false);
        isLoadingRef.current = false;
        setIframeError(false);
    };

    const handleError = () => {
        clearWatchdog();
        setIsIframeLoading(false);
        isLoadingRef.current = false;
        setIframeError(true);
    };

    const retryLoad = () => {
        const url = iframeRef.current?.src || livePreviewUrl;
        loadUrl(url);
    };

    // ── Render states ──────────────────────────────────────────────────────

    const renderPlaceholder = (icon: React.ReactNode, title: string, body: string, action?: React.ReactNode) => (
        <div className="text-center px-6">
            <div className="mx-auto mb-5 flex items-center justify-center">{icon}</div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            <p className="text-sm text-gray-600 max-w-sm mx-auto mb-4">{body}</p>
            {action}
        </div>
    );

    const spinnerIcon = (
        <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                    <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
                        <Globe className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute inset-0 animate-spin">
                        <div className="w-full h-full rounded-2xl border-4 border-transparent border-t-secondary border-r-secondary/60" />
                    </div>
                </div>
            </div>
        </div>
    );

    const btnClass = 'px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:opacity-90 active:scale-95 transition-all';

    const loadingState = (
        <div className="text-center px-6">
            {spinnerIcon}
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Loading Preview</h3>
            <p className="text-sm text-gray-600 max-w-sm mx-auto">Setting up your store preview. This may take a moment…</p>
        </div>
    );

    return (
        <div className="flex-1 flex items-center justify-center bg-gray-50 overflow-hidden relative">
            {entityLoading ? loadingState
            : entityError ? renderPlaceholder(
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-error" />
                </div>,
                'Failed to Load Preview',
                'Could not connect to your store. Please try refreshing the page.',
                <button onClick={() => window.location.reload()} className={btnClass}>Refresh Page</button>
            ) : entityId === undefined || entityId === null ? renderPlaceholder(
                <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-amber-600" />
                </div>,
                'Store Not Found',
                'Your store information could not be loaded. Please contact support.',
                <button onClick={() => navigate('/dashboard')} className={btnClass}>Back to Dashboard</button>
            ) : !isPreviewReady ? loadingState
            : iframeError ? renderPlaceholder(
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-error" />
                </div>,
                'Preview Server Unavailable',
                'The preview server is currently unavailable (timeout or connection error).',
                <div className="flex flex-col gap-3">
                    <button onClick={retryLoad} className={btnClass}>Try Again</button>
                    <p className="text-xs text-gray-500">If this persists, please contact support to restart the preview server.</p>
                </div>
            ) : livePreviewUrl === 'about:blank' ? renderPlaceholder(
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                    <Wand2 className="w-8 h-8 text-primary" />
                </div>,
                'Your Preview Awaits',
                'Start chatting with the AI to customize your store. Your preview will appear here in real time.',
                undefined
            ) : (
                <div className="relative w-full h-full">
                    {/* Building animation overlay */}
                    {showBuildingAnimation && (
                        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-xl">
                            <div className="text-center px-6">
                                <div className="relative w-20 h-20 mx-auto mb-5">
                                    <div className="absolute inset-0 bg-primary/10 rounded-2xl animate-pulse" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Wand2 className="w-11 h-11 text-primary animate-bounce" />
                                    </div>
                                </div>
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Making changes…</h3>
                                <p className="text-sm text-gray-600 max-w-md mx-auto">
                                    The AI is updating your store. Changes will appear in a moment.
                                </p>
                                <div className="flex items-center justify-center gap-1.5 mt-5">
                                    {[0, 150, 300].map(delay => (
                                        <div
                                            key={delay}
                                            className="w-2 h-2 rounded-full bg-primary animate-bounce"
                                            style={{ animationDelay: `${delay}ms` }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* iframe loading overlay */}
                    {isIframeLoading && !showBuildingAnimation && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50 rounded-xl">
                            <div className="text-center px-6">
                                <div className="w-16 h-16 mx-auto mb-4 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
                                    <RefreshCw className="w-8 h-8 text-white animate-spin" />
                                </div>
                                <h3 className="text-sm font-semibold text-gray-900 mb-1">Loading Preview</h3>
                                <p className="text-xs text-gray-600">Loading your store preview…</p>
                            </div>
                        </div>
                    )}

                    <iframe
                        key={iframeSrc}
                        ref={iframeRef}
                        src={iframeSrc}
                        title="Live Store Preview"
                        className="w-full h-full border-none bg-white"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                        onLoad={handleLoad}
                        onError={handleError}
                    />
                </div>
            )}
        </div>
    );
}
